import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  flexRender,
  type SortingState,
  type ColumnDef,
  type PaginationState,
  type RowSelectionState,
  type ColumnFiltersState,
  type VisibilityState,
  type ColumnOrderState,
  type Row,
  type FilterFnOption,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/table/EmptyState";
import { TableSkeletonRows } from "@/components/shared/TableSkeleton";
import { exportToCSV, type CSVExportColumn } from "@/utils/csvExport";
import { errorToastText } from "@/lib/translateBackendError";
import { logger } from "@/lib/sentry";
import { format as formatDateFns } from "date-fns";
import { calculateColumnWidth } from "@/utils/columnUtils";
import { parseDateString } from "@/utils/dateUtils";
import { formatDateDisplay, formatDateTime, getLocaleTag } from "@/lib/format";
import { emailRegex } from "@/utils/validationUtils";
import {
  CalendarIcon,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Download,
  Inbox,
  Loader2,
  Mail,
  Pencil,
  Plus,
  SearchX,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import type {
  BaseEditableTableProps,
  EditableCellConfig,
  FilterValue,
  TableDensity,
} from "./types";
import { EditableCell } from "./EditableCell";
import { hasValueChanged, type CellValue } from "./utils/cellValueUtils";
import { TableSearch } from "./TableSearch";
import { FilterDropdown } from "./FilterDropdown";
import { FilterTag } from "./FilterTag";
import { useTableFilters } from "@/hooks/useTableFilters";
import { useTablePreferences } from "@/hooks/useTablePreferences";
import { rankItem, rankings, type RankingInfo } from "@tanstack/match-sorter-utils";
import { Trans } from "@lingui/react";
import { useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";
import { TableColumnManager } from "./TableColumnManager";

// Helper function to generate a reliable column ID
function generateColumnId<T>(
  config: EditableCellConfig<T>,
  index: number
): string {
  // Use config.id if available
  if (config.id && config.id.trim() !== "") {
    return config.id;
  }

  // Use accessorKey if available and not empty
  if (config.accessorKey && String(config.accessorKey).trim() !== "") {
    return String(config.accessorKey);
  }

  // Fallback to a generated unique ID
  return `column-${index}`;
}

// Reduce a row's stored value for a column into the space the editor works in.
//
// `getEditValue` maps a stored value to what the editor shows (cents -> "10.00",
// an ISO instant -> "09:00"), so change-detection has to compare like with like.
// Without this the baseline stays in stored-space and never equals the editor's
// value, so merely opening a cell and clicking away counts as an edit.
function getComparableOriginal<T>(
  row: T,
  config: EditableCellConfig<T>
): CellValue {
  const raw = row[config.accessorKey] as CellValue;
  return config.getEditValue ? (config.getEditValue(raw) as CellValue) : raw;
}

// Resolve a column's editability for one concrete row.
//
// `editable` may be a predicate, which is the only way a column can be editable on
// rows that carry the backing record and read-only on rows that do not. Without a
// row-aware check the table opens an editor over a cell whose update handler has
// nothing to write to, and the typed value is silently discarded.
function isCellEditable<T>(config: EditableCellConfig<T>, row: T): boolean {
  if (typeof config.editable === "function") {
    return config.editable(row);
  }
  return config.editable !== false;
}

// Column label for a cell's accessible name.
//
// The editable wrapper is a bare <div role="button">: without a name a screen reader
// announces "button" and nothing else, so the user cannot tell which column the
// editor would open on. `header` may be a React element (a <Trans>), which has no
// reliable text form here, and an icon-only column carries `header: ""` — both fall
// back to the column key, which at least names the field.
function getColumnLabel<T>(config: EditableCellConfig<T>): string {
  if (typeof config.header === "string" && config.header.trim() !== "") {
    return config.header;
  }
  return String(config.accessorKey);
}

// Plain text of a column header, for filter chips, export headers and the save
// announcement. A `<Trans>` header has its text in nested children; an element
// whose text cannot be found yields "".
function getHeaderText(header: string | React.ReactElement): string {
  if (typeof header === "string") return header;
  if (!React.isValidElement(header)) return "";
  const extractText = (element: React.ReactElement): string => {
    const props = element.props as { children?: React.ReactNode };
    if (typeof props.children === "string") return props.children;
    if (React.isValidElement(props.children)) return extractText(props.children);
    if (Array.isArray(props.children)) {
      return props.children
        .map((child: React.ReactNode) => {
          if (typeof child === "string") return child;
          if (React.isValidElement(child)) return extractText(child);
          return "";
        })
        .join("");
    }
    return "";
  };
  return extractText(header);
}

const ISO_DATE_PREFIX = /^\d{4}-\d{2}-\d{2}/;

// The text a CSV cell gets when the caller passes no `exportColumns`: the same
// display text the table shows (formatter, then select label, then the locale
// date), and readable text for arrays such as labels instead of "[object Object]".
function getExportCellText<T>(
  config: EditableCellConfig<T>,
  value: unknown,
  row: T,
  locale: string
): string {
  if (value === null || value === undefined) return "";
  if (config.formatter) return config.formatter(value, row);
  const options = config.selectOptions ?? config.enhancedSelectOptions;
  const optionLabel = options?.find((option) => option.value === value)?.label;
  if (optionLabel) return optionLabel;
  if (
    value instanceof Date ||
    (typeof value === "string" &&
      (config.inputType === "date" || ISO_DATE_PREFIX.test(value)))
  ) {
    return formatCellDate(value, config.dateFormat, locale);
  }
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item && typeof item === "object") {
          const named = item as { label?: unknown; name?: unknown };
          return String(named.label ?? named.name ?? "");
        }
        return String(item);
      })
      .filter((text) => text !== "")
      .join(", ");
  }
  if (typeof value === "object") return "";
  return String(value);
}

// Classes per row density. `compact` keeps a row near 36px (py-1.5 around a 24px
// cell); `comfortable` is the roomier pre-density look. Inline editors keep their
// own 40px height, so a compact row grows while a cell is open.
const DENSITY_STYLES: Record<
  TableDensity,
  { cell: string; cellHeight: string; cellMinHeight: string; header: string }
> = {
  compact: {
    cell: "px-2 py-1.5 text-sm",
    cellHeight: "h-6",
    cellMinHeight: "min-h-6",
    header: "h-8",
  },
  comfortable: {
    cell: "px-2 py-3",
    cellHeight: "h-8",
    cellMinHeight: "min-h-8",
    header: "h-10",
  },
};

// How long a saved cell stays tinted. Long enough to see, short enough not to
// read as a state.
const SAVE_FLASH_MS = 600;

// Pencil shown while the pointer or focus is on an editable cell, so editable
// cells are told apart from read-only ones without a legend. Decorative: the
// wrapper's "Edit {column}" name already says it.
function EditHint({ position }: { position: "start" | "end" }) {
  return (
    <Pencil
      aria-hidden="true"
      data-edit-hint=""
      className={cn(
        "h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100",
        position === "end" ? "ml-auto pl-1" : "mr-auto pr-1"
      )}
    />
  );
}

// What this column's editor opens, so AT announces the popup BEFORE the user
// activates the cell rather than after. Text, number and email editors open
// nothing, so they carry no aria-haspopup at all.
function getCellHasPopup<T>(
  config: EditableCellConfig<T>
): "listbox" | "dialog" | undefined {
  switch (config.inputType) {
    case "select":
    case "enhanced-select":
    case "multi-select":
    case "tag-select":
      return "listbox";
    case "date":
      return "dialog";
    default:
      return undefined;
  }
}

// The display text of a `date` cell. `config.dateFormat` may carry
// `Intl.DateTimeFormatOptions`, and then the cell shows that format; a string format has
// no formatter behind it, so it falls back to the app-wide dd.MM.yyyy display. The default cell renderer and the accessible name both go through
// here, so a screen reader announces the text the cell shows.
function formatCellDate(
  value: Date | string,
  dateFormat: string | Intl.DateTimeFormatOptions | undefined,
  locale: string
): string {
  if (dateFormat && typeof dateFormat !== "string") {
    const date = value instanceof Date ? value : parseDateString(value);
    return date ? date.toLocaleDateString(getLocaleTag(locale), dateFormat) : "";
  }
  return formatDateDisplay(value);
}

// The value half of a cell's accessible name.
//
// `row[accessorKey]` is the STORED value, which is rarely the text on screen: a
// select column stores a code (`varianceDisposition` holds "write_off", the cell
// shows "Write off"), a date column stores "2019-03-05T00:00:00Z" over a rendered
// "05.03.2019", a time column stores a full timestamp over a rendered "08:00". A
// screen reader must hear what the table shows, so the same conversions the default
// wrapper applies to `displayValue` are applied here, in the same order.
// Non-primitive values (objects, arrays) have no useful text form and would
// announce "[object Object]", so they fall back to the empty label.
function getCellAccessibleValue<T>(
  config: EditableCellConfig<T>,
  row: T,
  emptyLabel: string,
  locale: string
): string {
  const raw = row[config.accessorKey];
  if (raw === null || raw === undefined) return emptyLabel;
  if (typeof raw === "object") return emptyLabel;
  const asText = String(raw).trim();
  if (asText === "") return emptyLabel;

  // A custom formatter outranks every built-in conversion, as it does for display.
  if (config.formatter) {
    const formatted = config.formatter(raw, row);
    if (typeof formatted === "string" && formatted.trim() !== "") {
      return formatted.trim();
    }
  }

  const options = config.selectOptions ?? config.enhancedSelectOptions;
  const optionLabel = options?.find((option) => option.value === asText)?.label;
  if (optionLabel) return optionLabel;

  if (config.inputType === "date") {
    const formatted = formatCellDate(asText, config.dateFormat, locale);
    if (formatted) return formatted;
  }

  // A time column keeps its display conversion in `getEditValue` — the cell and the
  // editor both render its result, so it is the only handle the shared table has on
  // what the user actually sees.
  if (config.inputType === "time" && config.getEditValue) {
    const formatted = config.getEditValue(raw);
    if (typeof formatted === "string" && formatted.trim() !== "") {
      return formatted.trim();
    }
  }

  return asText;
}

// Did this event start on an interactive control the cell renderer put inside the
// wrapper (a delete <Button>, a link) rather than on the cell itself?
//
// Such an event bubbles up to the wrapper, and acting on it opens the editor on top
// of the control the user just activated — with a keyboard the editor even replaces
// the button mid-activation. Plain rendered content (spans, badges) must still open
// the editor, so only interactive descendants are ignored. `closest` walks up to the
// wrapper itself, which carries role="button" too, hence the currentTarget check.
const INTERACTIVE_CELL_CHILD_SELECTOR =
  'button, a[href], input, select, textarea, [role="button"], [role="link"], [role="checkbox"], [role="switch"], [role="combobox"]';

// The nodes inside an open editor that can hold focus. Disabled controls are
// excluded on purpose: `focus()` on a disabled control is a silent no-op, so
// picking one would leave the user on document.body — exactly the bug this
// selector exists to prevent.
const EDITOR_FOCUSABLE_SELECTOR =
  'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

function isInteractiveChildEvent(
  event: React.SyntheticEvent<HTMLDivElement>
): boolean {
  const target = event.target as HTMLElement | null;
  if (!target || target === event.currentTarget) return false;
  if (typeof target.closest !== "function") return false;
  const control = target.closest(INTERACTIVE_CELL_CHILD_SELECTOR);
  return control !== null && control !== event.currentTarget;
}

// Helper function to validate and clean column order
function validateColumnOrder(
  proposedOrder: string[],
  allColumnIds: string[]
): string[] {
  // Remove duplicates while preserving order
  const uniqueOrder = Array.from(new Set(proposedOrder));

  // Filter out invalid column IDs (ones that don't exist in allColumnIds)
  const validOrder = uniqueOrder.filter((id) => allColumnIds.includes(id));

  // Insert missing columns at their intended position based on allColumnIds order
  const missingColumns = allColumnIds.filter((id) => !validOrder.includes(id));
  if (missingColumns.length === 0) {
    return validOrder;
  }

  const result = [...validOrder];
  for (const missing of missingColumns) {
    const intendedIndex = allColumnIds.indexOf(missing);
    // Find the best insertion point: right after the preceding column from allColumnIds
    let insertAt = result.length;
    for (let i = intendedIndex - 1; i >= 0; i--) {
      const precedingId = allColumnIds[i];
      const posInResult = result.indexOf(precedingId);
      if (posInResult !== -1) {
        insertAt = posInResult + 1;
        break;
      }
    }
    result.splice(insertAt, 0, missing);
  }

  return result;
}

// Timestamp and date fields stay out of the global search to avoid confusing number matches
const excludeFromSearch = ["createdAt", "updatedAt", "birthdate"];

// The text the global search compares for one cell, or null when the column is
// excluded. Select columns are searched by their label, not their stored value.
function searchableCellText<T>(
  row: Row<T>,
  columnId: string,
  columnOptions?: { columns?: EditableCellConfig<T>[]; locale?: string }
): string | null {
  if (excludeFromSearch.includes(columnId)) {
    return null;
  }

  const cellValue = row.getValue(columnId);
  let searchValue = cellValue;

  // If we have column configuration, check if this is a select column
  if (columnOptions?.columns) {
    const columnConfig = columnOptions.columns.find(
      (col) => col.accessorKey === columnId
    );
    if ((columnConfig?.inputType === "select" || columnConfig?.inputType === "enhanced-select") && columnConfig.selectOptions) {
      // Find the label for the current value
      const option = columnConfig.selectOptions.find(
        (opt) => opt.value === cellValue
      );
      if (option) {
        searchValue = option.label;
      }
    }
  }

  // Handle null/undefined, objects, React elements, and other non-string types
  if (searchValue == null) {
    return "";
  }
  if (typeof searchValue === "string" || typeof searchValue === "number") {
    return String(searchValue);
  }
  if (searchValue instanceof Date) {
    return formatDateDisplay(searchValue);
  }
  // For objects, React elements, or other complex types, convert to string
  // but avoid "[object Object]" by using JSON.stringify for plain objects
  if (typeof searchValue === "object" && searchValue.constructor === Object) {
    try {
      return JSON.stringify(searchValue);
    } catch {
      return String(searchValue);
    }
  }
  return String(searchValue);
}

// CONTAINS, not match-sorter's default MATCHES: MATCHES passes any text that
// holds the search letters in order with gaps, so "ismail" matched every
// "...s...@gmail.com" address. Case and diacritics are still ignored.
const searchRankOptions = { threshold: rankings.CONTAINS } as const;

// Per-row result for multi-word searches; the global filter calls once per
// column, so the row-wide check runs once per row and search value.
const multiTermRowMatch = new WeakMap<object, { value: string; passed: boolean }>();

// Global search: one word must appear in a cell. Several words must each appear
// in some searchable cell of the row, in any order ("karima duvaud" finds first
// name Karima with last name Duvaud).
function fuzzyFilter<T>(
  row: Row<T>,
  columnId: string,
  value: string,
  addMeta: (meta: { itemRank: RankingInfo }) => void,
  columnOptions?: { columns?: EditableCellConfig<T>[]; locale?: string }
) {
  const cellText = searchableCellText(row, columnId, columnOptions);
  if (cellText == null) {
    return false;
  }

  const itemRank = rankItem(cellText, value, searchRankOptions);
  addMeta({ itemRank });

  const terms = String(value ?? "").trim().split(/\s+/).filter(Boolean);
  if (terms.length <= 1) {
    return itemRank.passed;
  }

  const cached = multiTermRowMatch.get(row);
  if (cached && cached.value === value) {
    return cached.passed;
  }
  const rowTexts = row
    .getAllCells()
    .filter((cell) => cell.column.getCanGlobalFilter())
    .map((cell) => searchableCellText(row, cell.column.id, columnOptions))
    .filter((text): text is string => text != null && text !== "");
  const passed = terms.every((term) =>
    rowTexts.some((text) => rankItem(text, term, searchRankOptions).passed)
  );
  multiTermRowMatch.set(row, { value, passed });
  return passed;
}

// Custom filter function for multi-select filters
function multiSelectFilter<T>(
  row: Row<T>,
  columnId: string,
  filterValue: string[] | null | undefined
) {
  if (!filterValue || !Array.isArray(filterValue) || filterValue.length === 0) {
    return true; // Show all rows if no filter
  }

  const cellValue = row.getValue(columnId);
  // `getValue` is `unknown`; `some(===)` is `includes` without the `string` parameter
  // (they differ only for NaN, which no filter list carries).
  return filterValue.some((candidate) => candidate === cellValue);
}

// Custom filter function for label multi-select filters (labels are arrays of objects)
function labelsFilter<T>(
  row: Row<T>,
  columnId: string,
  filterValue: string[] | null | undefined
) {
  if (!filterValue || !Array.isArray(filterValue) || filterValue.length === 0) {
    return true;
  }
  const labels = row.getValue(columnId) as Array<{ id?: string }> | null;
  if (!Array.isArray(labels)) return false;
  return filterValue.some(filterId => labels.some(l => l.id === filterId));
}

// Custom filter function for date range filters
function dateRangeFilter<T>(
  row: Row<T>,
  columnId: string,
  filterValue: { from?: Date; to?: Date } | null | undefined
) {
  if (!filterValue || typeof filterValue !== "object") {
    return true; // Show all rows if no filter
  }

  const { from, to } = filterValue as { from?: Date; to?: Date };
  if (!from && !to) return true;

  const cellValue = row.getValue(columnId);
  const date =
    cellValue instanceof Date ? cellValue : new Date(cellValue as string);

  if (isNaN(date.getTime())) return false; // Invalid date

  if (from && to) {
    return date >= from && date <= to;
  } else if (from) {
    return date >= from;
  } else if (to) {
    return date <= to;
  }

  return true;
}

const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

// Builds the "Rows per page" choices, always including the size currently in
// effect so the Select trigger has a matching item to render.
function pageSizeOptions(current: number): number[] {
  if (DEFAULT_PAGE_SIZE_OPTIONS.includes(current)) {
    return DEFAULT_PAGE_SIZE_OPTIONS;
  }
  return [...DEFAULT_PAGE_SIZE_OPTIONS, current].sort((a, b) => a - b);
}

export function BaseEditableTable<T extends { id?: string }>({
  data,
  columns,
  onCellUpdate,
  defaultSortField,
  defaultSortOrder = "asc",
  defaultPageSize = DEFAULT_PAGE_SIZE,
  enableSelection,
  selectedRows,
  onSelectionChange,
  renderSelectionActions,
  enableSearch = false,
  searchPlaceholder,
  enableFilters = false,
  filterConfig,
  onFilterChange,
  onSearchChange,
  searchTerm,
  enableColumnVisibility = false,
  enableColumnOrdering = false,
  tableId,
  defaultColumnVisibility,
  defaultColumnOrder,
  onColumnVisibilityChange,
  onColumnOrderChange,
  defaultColumnFilters,
  onSortingChange: externalOnSortingChange,
  sorting: externalSorting,
  forceDefaults = false,
  enableVirtualization = false,
  estimatedRowHeight = 48,
  overscan = 5,
  // Server-side pagination props
  serverSidePagination = false,
  totalRows,
  pagination: externalPagination,
  onPaginationChange: externalOnPaginationChange,
  isFetching = false,
  toolbarLeft,
  toolbarRight,
  toolbarBelowRow,
  searchMaxWidth,
  onRowClick,
  hidePagination = false,
  rowClassName,
  emptyState,
  filteredEmptyState,
  hasExternalFilters = false,
  enableGridKeyboardNav = false,
  isLoading = false,
  density: densityProp,
  enableExport = false,
  exportAll,
  exportFileName,
  exportColumns,
  onAdd,
  addLabel,
}: BaseEditableTableProps<T>) {
  const { i18n } = useLingui();
  // Shared row-click handler — used by both the virtualized and
  // non-virtualized render paths so behavior stays consistent. Skips clicks
  // originating from nested interactive controls so they don't double-fire.
  const handleRowClick = useCallback(
    (event: React.MouseEvent<HTMLTableRowElement>, rowOriginal: T) => {
      if (!onRowClick) return;
      const target = event.target as HTMLElement | null;
      if (
        target?.closest(
          'input, textarea, select, button, a, [role="button"], [data-no-row-click]'
        )
      ) {
        return;
      }
      onRowClick(rowOriginal);
    },
    [onRowClick]
  );
  const [internalSorting, setInternalSorting] = useState<SortingState>(
    defaultSortField
      ? [{ id: defaultSortField as string, desc: defaultSortOrder === "desc" }]
      : []
  );
  // A server-side-sorted table passes `sorting` and owns the order; everything else
  // keeps the internal state. `setSorting` still writes the internal state so an
  // uncontrolled table is unchanged, and a controlled one simply re-renders from
  // whatever the parent passes next.
  const sorting = externalSorting ?? internalSorting;
  const setSorting = setInternalSorting;
  // Internal pagination state (used when not using server-side pagination)
  const [internalPagination, setInternalPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });

  // Use external pagination state when server-side pagination is enabled
  const pagination = serverSidePagination && externalPagination
    ? externalPagination
    : internalPagination;

  const setPagination = serverSidePagination && externalOnPaginationChange
    ? externalOnPaginationChange
    : setInternalPagination;

  // A new search, filter or sort describes a different result set, so page 3 of
  // the old one means nothing. Without this a server-paged list on page 3 asks
  // for offset 20 of a two-row result and shows an empty table (frontend.md).
  // Read through a ref so the callers below do not re-run on every page change.
  const paginationRef = useRef(pagination);
  paginationRef.current = pagination;
  const resetPageIndex = useCallback(() => {
    if (paginationRef.current.pageIndex === 0) return;
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [setPagination]);
  const [internalRowSelection, setInternalRowSelection] =
    useState<RowSelectionState>({});
  const [editingCell, setEditingCell] = useState<{
    rowId: string;
    column: keyof T;
  } | null>(null);
  const [editValue, setEditValue] = useState<CellValue>(null);

  // Value an editor has typed but deliberately NOT pushed into `editValue`.
  //
  // `editValue` is a dependency of the column memo, so writing it re-creates the
  // column defs and remounts the cell — which drops focus after a single
  // keystroke. Editors that need to report every keystroke (the time cell, whose
  // commit must see the current value) write here instead: a ref costs no
  // render. Commit paths must prefer this over `editValueRef`.
  //
  // Cleared whenever a cell opens or editing ends, so a stale value from a
  // previous cell can never be committed into the next one.
  const pendingEditRef = useRef<CellValue>(null);
  // Cell key (`rowId::column`) whose editor was opened from the keyboard, so focus
  // can return there when the editor closes. A mouse-opened edit leaves focus where
  // the pointer put it — restoring there would yank focus off whatever the user
  // clicked next.
  const keyboardOpenRef = useRef<string | null>(null);
  // This table's own outer element, so the two focus effects below search only their
  // own cells. Several screens mount more than one BaseEditableTable at a time
  // (SubsidyLedgerTab renders four, ChildConsentsSection and PortfolioScaleManager
  // two), and a document-wide lookup would hand this table's focus to a sibling's
  // open editor.
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  // Cells whose last commit failed validation, keyed `rowId::column`. Kept separate from
  // `validationError` (which only lives as long as the editor is open) so that a cell the
  // user has already navigated away from stays visibly flagged — non-blocking validation
  // must still leave a trace, or an invalid entry disappears silently.
  const [invalidCells, setInvalidCells] = useState<Map<string, string>>(
    () => new Map()
  );
  // The cell that just saved, tinted for SAVE_FLASH_MS, and the matching text
  // for the polite live region. A flash alone says nothing to a screen reader.
  const [flashCellKey, setFlashCellKey] = useState<string | null>(null);
  const [saveAnnouncement, setSaveAnnouncement] = useState("");
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    },
    []
  );

  // Initialize table filters hook
  const {
    globalFilter,
    setGlobalFilter,
    handleColumnFiltersChange: updateColumnFilters,
    _internalGlobalFilter,
    _internalColumnFilters,
  } = useTableFilters({ initialColumnFilters: defaultColumnFilters });

  // Initialize table preferences hook
  const {
    preferences,
    updateColumnVisibility: updatePersistedVisibility,
    updateColumnOrder: updatePersistedOrder,
    updatePageSize: updatePersistedPageSize,
    updateDensity,
    clearPreferences,
  } = useTablePreferences(tableId);
  const density: TableDensity =
    (tableId ? preferences.density : undefined) ?? densityProp ?? "compact";
  const densityStyles = DENSITY_STYLES[density];

  // Column visibility state
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      if (forceDefaults) {
        return defaultColumnVisibility || {};
      }
      if (tableId && preferences.columnVisibility) {
        return preferences.columnVisibility;
      }
      return defaultColumnVisibility || {};
    }
  );

  // Column order state
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(() => {
    if (forceDefaults && defaultColumnOrder) {
      return defaultColumnOrder;
    }
    if (tableId && preferences.columnOrder) {
      return preferences.columnOrder;
    }
    if (defaultColumnOrder) {
      return defaultColumnOrder;
    }
    // If no default order, create one from column IDs using safe generation
    const baseOrder = columns.map((col, index) => generateColumnId(col, index));
    // Ensure select and avatar are first if selection is enabled
    if (enableSelection) {
      return ["select", ...baseOrder];
    }
    return baseOrder;
  });

  // Sync preferences when tableId changes or preferences are loaded
  useEffect(() => {
    if (tableId && preferences.columnVisibility) {
      setColumnVisibility(preferences.columnVisibility);
    }
  }, [tableId, preferences.columnVisibility]);

  useEffect(() => {
    if (tableId && preferences.columnOrder) {
      // Get all current column IDs for validation
      const allColumnIds = columns.map((col, index) =>
        generateColumnId(col, index)
      );
      if (enableSelection) {
        allColumnIds.unshift("select");
      }

      // Validate the stored order to prevent corruption
      const validatedOrder = validateColumnOrder(
        preferences.columnOrder,
        allColumnIds
      );

      // Ensure fixed columns are always first when loading from preferences
      const fixedIds = enableSelection ? ["select", "avatar"] : ["avatar"];
      const fixedInOrder = fixedIds.filter((id) => validatedOrder.includes(id));
      const nonFixedInOrder = validatedOrder.filter(
        (id) => !fixedIds.includes(id)
      );
      const finalOrder = [...fixedInOrder, ...nonFixedInOrder];
      setColumnOrder(finalOrder);
    }
  }, [tableId, preferences.columnOrder, enableSelection, columns]);

  useEffect(() => {
    if (tableId && preferences.pageSize) {
      setPagination((prev) => ({ ...prev, pageSize: preferences.pageSize! }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setPagination is conditionally stable, adding it would cause re-runs
  }, [tableId, preferences.pageSize]);

  // Force default visibility when props change in force mode
  useEffect(() => {
    if (forceDefaults && defaultColumnVisibility) {
      setColumnVisibility(defaultColumnVisibility);
    }
  }, [forceDefaults, defaultColumnVisibility]);

  // Force default order when props change in force mode
  useEffect(() => {
    if (forceDefaults && defaultColumnOrder) {
      setColumnOrder(defaultColumnOrder);
    }
  }, [forceDefaults, defaultColumnOrder]);

  // Use controlled or internal row selection with proper updater handling
  const rowSelection = selectedRows ?? internalRowSelection;

  const handleRowSelectionChange = useCallback(
    (
      updaterOrValue:
        | RowSelectionState
        | ((prev: RowSelectionState) => RowSelectionState)
    ) => {
      const newValue =
        typeof updaterOrValue === "function"
          ? updaterOrValue(rowSelection)
          : updaterOrValue;

      if (onSelectionChange) {
        onSelectionChange(newValue);
      } else {
        setInternalRowSelection(newValue);
      }
    },
    [rowSelection, onSelectionChange]
  );

  // The input value updates on every keystroke; `useTableFilters` hands the table
  // a copy debounced by 300 ms (`_internalGlobalFilter`), which drives the
  // client-side filter and, below, `onSearchChange`.
  const handleGlobalFilterChange = useCallback(
    (value: string | ((prev: string) => string)) => {
      setGlobalFilter(typeof value === "function" ? value(globalFilter) : value);
    },
    [globalFilter, setGlobalFilter]
  );

  // The search text last agreed with the caller, either reported through
  // `onSearchChange` or received through `searchTerm`. Comparing against it keeps
  // a caller-provided value (a restored URL) from being echoed back as a user
  // edit, which would also reset the page the URL asked for.
  const lastSearchRef = useRef("");
  useEffect(() => {
    if (_internalGlobalFilter === lastSearchRef.current) return;
    lastSearchRef.current = _internalGlobalFilter;
    onSearchChange?.(_internalGlobalFilter);
    resetPageIndex();
  }, [_internalGlobalFilter, onSearchChange, resetPageIndex]);
  useEffect(() => {
    if (searchTerm === undefined || searchTerm === lastSearchRef.current) return;
    lastSearchRef.current = searchTerm;
    setGlobalFilter(searchTerm);
  }, [searchTerm, setGlobalFilter]);

  // Handle column filter changes - use current table state for external control
  const handleColumnFiltersChange = useCallback(
    (
      updaterOrValue:
        | ColumnFiltersState
        | ((prev: ColumnFiltersState) => ColumnFiltersState)
    ) => {
      // Resolve the updater function using current table state
      const resolvedValue =
        typeof updaterOrValue === "function"
          ? updaterOrValue(_internalColumnFilters)
          : updaterOrValue;

      // Update internal state
      updateColumnFilters(resolvedValue);
      resetPageIndex();

      // Notify external handler if provided
      if (onFilterChange) {
        onFilterChange(resolvedValue);
      }
    },
    [onFilterChange, _internalColumnFilters, updateColumnFilters, resetPageIndex]
  );

  // Handle column visibility changes
  const handleColumnVisibilityChange = useCallback(
    (
      updaterOrValue:
        | VisibilityState
        | ((prev: VisibilityState) => VisibilityState)
    ) => {
      const resolvedValue =
        typeof updaterOrValue === "function"
          ? updaterOrValue(columnVisibility)
          : updaterOrValue;

      setColumnVisibility(resolvedValue);

      // Persist to localStorage if tableId is provided
      if (tableId) {
        updatePersistedVisibility(resolvedValue);
      }

      // Notify external handler if provided
      if (onColumnVisibilityChange) {
        onColumnVisibilityChange(resolvedValue);
      }
    },
    [
      columnVisibility,
      tableId,
      updatePersistedVisibility,
      onColumnVisibilityChange,
    ]
  );

  // Get selected row IDs for external use
  const selectedRowIds = useMemo(() => {
    return Object.keys(rowSelection).filter((key) => rowSelection[key]);
  }, [rowSelection]);

  // Clear selection function
  const clearSelection = useCallback(() => {
    handleRowSelectionChange(() => ({}));
  }, [handleRowSelectionChange]);

  // Calculate minimum column widths based on actual content
  const getColumnWidth = useCallback(
    (config: EditableCellConfig<T>) => calculateColumnWidth(config, data),
    [data]
  );

  const handleCancel = useCallback(() => {
    pendingEditRef.current = null;
    setEditingCell(null);
    setEditValue(null);
    setValidationError(null);
  }, []);

  // Click-outside detection to unfocus dropdowns and other edit fields
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (editingCell && event.target instanceof Element) {
        // Check if the click is on a portal element (Select dropdown, DatePicker calendar, etc.)
        // These elements are rendered outside the DOM tree but should not close the editor
        const clickedElement = event.target as Element;
        const isPortalClick =
          clickedElement.closest("[data-radix-popper-content-wrapper]") || // Radix UI portals
          clickedElement.closest('[role="listbox"]') || // Select dropdown
          clickedElement.closest('[role="dialog"]') || // DatePicker calendar
          clickedElement.closest("[data-radix-portal]"); // Any Radix portal

        if (isPortalClick) {
          // Don't close if clicking on portal elements
          return;
        }

        // This table's own container, never a document-wide lookup: on a page
        // with several tables the first match is a sibling, and a click inside
        // this table's editor would count as "outside" and cancel the edit.
        const tableContainer = tableContainerRef.current;
        if (tableContainer && !tableContainer.contains(event.target)) {
          // Click is completely outside the table, cancel editing.
          // Drop the keyboard-open marker first: the user just clicked something
          // else, and pulling focus back into the table would steal it from
          // whatever they clicked.
          keyboardOpenRef.current = null;
          handleCancel();
          return;
        }

        // Check if click is outside the current editing cell
        const editingElement = tableContainer?.querySelector(
          '[data-editing-cell="true"]'
        );
        if (editingElement && !editingElement.contains(event.target)) {
          // Click is outside the editing cell but within the table, cancel editing.
          // Same reason as above: a pointer dismissal must not restore focus.
          keyboardOpenRef.current = null;
          handleCancel();
        }
      }
    };

    let timeoutId: NodeJS.Timeout | null = null;
    let listenerAdded = false;

    if (editingCell) {
      // Add event listener with a small delay to avoid immediate cancellation
      timeoutId = setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
        listenerAdded = true;
      }, 100);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (listenerAdded) {
        document.removeEventListener("mousedown", handleClickOutside);
      }
    };
  }, [editingCell, handleCancel]);

  const handleCellClick = useCallback(
    (rowId: string, accessorKey: keyof T) => {
      const rowData = data.find((row) => row.id === rowId);

      // Get the column config to determine how to handle the value
      const columnConfig = columns.find(
        (col) => col.accessorKey === accessorKey
      );

      // Never open an editor over a cell this row cannot edit — the last line of
      // defence for row-aware `editable` predicates (grid navigation and any
      // caller-driven open both funnel through here).
      if (columnConfig && rowData && !isCellEditable(columnConfig, rowData)) {
        return;
      }

      pendingEditRef.current = null;
      setEditingCell({ rowId, column: accessorKey });
      const cellValue = rowData?.[accessorKey];

      // Apply getEditValue transformation if defined (e.g., convert cents to base currency)
      const transformedValue = columnConfig?.getEditValue
        ? columnConfig.getEditValue(cellValue)
        : cellValue;

      // Handle different input types appropriately
      let initialValue: string | Date | null;
      if (columnConfig?.inputType === "date" && transformedValue !== undefined && transformedValue !== null) {
        // For dates, keep as Date object or convert string to Date
        if (transformedValue instanceof Date) {
          initialValue = transformedValue;
        } else if (typeof transformedValue === "string") {
          initialValue = new Date(transformedValue);
        } else {
          initialValue = "";
        }
      } else {
        // For all other types (including select), convert to string
        initialValue = transformedValue !== undefined && transformedValue !== null ? String(transformedValue) : "";
      }

      setEditValue(initialValue);
      // Re-opening a cell that previously failed validation restores its message, so the
      // reason the value never saved is still visible when the user comes back to fix it.
      setValidationError(invalidCells.get(`${rowId}::${String(accessorKey)}`) ?? null);
    },
    [data, columns, invalidCells]
  );

  // Enter and Space are the two keys a role="button" must answer to (WAI-ARIA button
  // pattern). Both are prevented: Space would otherwise scroll the page, and Enter
  // inside a form-bearing page would submit it.
  const handleCellKeyDown = useCallback(
    (
      event: React.KeyboardEvent<HTMLDivElement>,
      rowId: string,
      accessorKey: keyof T
    ) => {
      // A custom cell renderer may put its own button or link inside this wrapper. Its
      // key events bubble here with `target` set to the child, and preventing the default
      // on them would kill the child's native activation — the delete buttons in the
      // settling-in tabs stopped opening their confirmation dialog. Only act on key
      // events the wrapper itself received.
      if (event.target !== event.currentTarget) return;
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      keyboardOpenRef.current = `${rowId}::${String(accessorKey)}`;
      handleCellClick(rowId, accessorKey);
    },
    [handleCellClick]
  );

  // Give focus back to the cell when a keyboard-opened editor closes. The editor
  // element is unmounted by then, so focus would otherwise fall to document.body and
  // the user would have to tab in from the top of the page again.
  useEffect(() => {
    if (editingCell) return;
    const key = keyboardOpenRef.current;
    if (!key) return;
    keyboardOpenRef.current = null;
    const scope = tableContainerRef.current;
    if (!scope) return;
    // Matched by walking the attribute rather than through a selector: a row id is
    // caller-supplied and may contain characters a CSS selector would have to escape.
    const target = Array.from(
      scope.querySelectorAll<HTMLElement>("[data-cell-key]")
    ).find((node) => node.dataset.cellKey === key);
    target?.focus();
  }, [editingCell]);

  // Give focus to a keyboard-opened editor. Only four of the nine editor branches
  // carry `autoFocus`, so without this Enter on a `time` or `enhanced-select` cell
  // opened the editor and left focus on document.body — the next Tab restarted at
  // the top of the page. Focusing from here covers every editor that renders the
  // wrapper, current and future, instead of five separate branches.
  //
  // It also covers grid navigation: `navigateGrid` keeps `keyboardOpenRef` set while
  // it moves, so a commit-and-move lands focus inside the next editor.
  useEffect(() => {
    if (!editingCell) return;
    // A pointer-opened edit must keep the focus the pointer gave it.
    if (!keyboardOpenRef.current) return;
    const scope = tableContainerRef.current;
    if (!scope) return;
    // Only one editor is mounted at a time within one table.
    const editor = scope.querySelector<HTMLElement>(
      '[data-editing-cell="true"]'
    );
    // The `select` editor renders no wrapper and autofocuses its own trigger.
    if (!editor) return;
    // An autoFocus editor (textarea, text/number, tag-select) already won.
    // Re-focusing would reset the textarea caret EditableCell just placed at the end.
    if (editor.contains(document.activeElement)) return;
    editor.querySelector<HTMLElement>(EDITOR_FOCUSABLE_SELECTOR)?.focus();
  }, [editingCell]);

  // Validation function
  const validateField = useCallback(
    (config: EditableCellConfig<T>, value: CellValue): string | null => {
      if (config.validation) {
        return config.validation(value as T[keyof T]);
      }
      return null;
    },
    []
  );

  // Handle cell save
  const handleSave = useCallback(
    async (
      rowId: string,
      field: keyof T,
      newValue: CellValue
    ): Promise<void> => {
      const config = columns.find((col) => col.accessorKey === field);
      if (!config) return;

      const cellKey = `${rowId}::${String(field)}`;
      const validationError = validateField(config, newValue);
      if (validationError) {
        setValidationError(validationError);
        setInvalidCells((prev) => new Map(prev).set(cellKey, validationError));
        return;
      }

      setIsSaving(true);
      setValidationError(null);

      try {
        // Read-only tables omit `onCellUpdate` (see BaseEditableTableProps); they
        // never open an editor, so this is unreachable there rather than silent.
        await onCellUpdate?.(rowId, field, newValue as T[keyof T]);
        setInvalidCells((prev) => {
          if (!prev.has(cellKey)) return prev;
          const next = new Map(prev);
          next.delete(cellKey);
          return next;
        });
        pendingEditRef.current = null;
        setEditingCell(null);
        setEditValue(null);
        setFlashCellKey(cellKey);
        if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
        flashTimerRef.current = setTimeout(
          () => setFlashCellKey(null),
          SAVE_FLASH_MS
        );
        setSaveAnnouncement(
          i18n._({
            id: "table.cell.saved",
            message: "{column} saved",
            values: {
              column: getHeaderText(config.header) || getColumnLabel(config),
            },
          })
        );
      } catch (error) {
        // The server's reason ("This group still has children") tells the user
        // what to fix; a bare "Failed to update" does not.
        const errorMessage = errorToastText(
          error,
          i18n,
          msg({ id: "table.cell.updateFailed", message: "Failed to update" })
        );
        logger.error("table cell update failed", {
          context: "BaseEditableTable",
          tableId,
          field: String(field),
          error,
        });
        toast.error(errorMessage);
        setValidationError(errorMessage);
      } finally {
        setIsSaving(false);
      }
    },
    [onCellUpdate, validateField, columns, i18n, tableId]
  );

  // Latest in-flight edit value, read synchronously by grid navigation (state is
  // stale inside the keydown callback the frame it fires).
  const editValueRef = useRef<CellValue>(editValue);
  editValueRef.current = editValue;


  // Row ids in the order the table renders them (post sort/filter/pagination). Populated
  // from the row model below, once the table instance exists.
  const visibleRowIdsRef = useRef<string[]>([]);

  // Accessor keys of the editable columns in the order the table actually RENDERS them —
  // visibility-aware and reordered. The raw `columns` prop is neither: navigating it walks
  // into columns the user has hidden and in an order that is not the one on screen.
  // Populated from the leaf-column model below, mirroring `visibleRowIdsRef`.
  const editableColumnKeysRef = useRef<(keyof T)[]>([]);

  // Spreadsheet keyboard navigation (opt-in via enableGridKeyboardNav). Commits the
  // current cell, then opens the next editable cell down (Enter) or right (Tab,
  // wrapping to the next row). Invalid values flag the cell but never trap focus —
  // the move proceeds regardless of whether the commit persisted.
  const navigateGrid = useCallback(
    async (direction: "down" | "up" | "right" | "left") => {
      const current = editingCell;
      if (!current) return;

      // Commit ONLY a genuine change. `handleSave` has no unchanged-value guard, so
      // committing unconditionally fires one mutation per cell merely tabbed through —
      // a whole row of pointless writes during a keyboard sweep. Compare in edit-space
      // via the same helpers the cell editors use (`getComparableOriginal` +
      // `hasValueChanged`), never the raw stored value.
      const currentConfig = columns.find(
        (c) => c.accessorKey === current.column
      );
      const currentRow = data.find((r) => r.id === current.rowId);
      // An editor that reports keystrokes into pendingEditRef holds a newer
      // value than editValue, which it never wrote to avoid a remount.
      const pendingValue = pendingEditRef.current ?? editValueRef.current;
      const changed =
        !currentConfig || !currentRow
          ? true // cannot establish a baseline — fall back to the previous behaviour
          : hasValueChanged(
              pendingValue instanceof Date
                ? pendingValue.toISOString()
                : String(pendingValue ?? ""),
              getComparableOriginal(currentRow, currentConfig),
              currentConfig.inputType ?? "text"
            );
      if (changed) {
        // A failed validation keeps the cell flagged but must not block the move
        // (non-blocking validation, S14).
        await handleSave(current.rowId, current.column, pendingValue);
      }

      const columnKeys = editableColumnKeysRef.current;
      // Walk the rows the table actually renders — sorted, filtered and paginated —
      // not the raw `data` prop. Navigating `data` sends the user to a row that is not
      // where they are looking as soon as any sort or filter is applied.
      const rowIds = visibleRowIdsRef.current;
      const rowIdx = rowIds.indexOf(current.rowId);
      const colIdx = columnKeys.indexOf(current.column);
      if (rowIdx === -1 || colIdx === -1) return;

      const step = (row: number, col: number) => {
        if (direction === "down") return { row: row + 1, col };
        if (direction === "up") return { row: row - 1, col };
        if (direction === "right") {
          return col + 1 >= columnKeys.length
            ? { row: row + 1, col: 0 }
            : { row, col: col + 1 };
        }
        return col - 1 < 0
          ? { row: row - 1, col: columnKeys.length - 1 }
          : { row, col: col - 1 };
      };

      const endEditing = () => {
        pendingEditRef.current = null;
        setEditingCell(null);
        setEditValue(null);
      };

      // Skip past cells this particular row cannot edit (row-aware `editable`
      // predicates), so navigation lands somewhere the user can actually type.
      let position = { row: rowIdx, col: colIdx };
      const maxSteps = rowIds.length * columnKeys.length + 1;
      for (let taken = 0; taken < maxSteps; taken++) {
        position = step(position.row, position.col);
        if (position.row >= rowIds.length || position.row < 0) {
          // Past either edge — end editing rather than wrapping around.
          endEditing();
          return;
        }
        const targetKey = columnKeys[position.col];
        const targetRow = data.find((r) => r.id === rowIds[position.row]);
        const targetConfig = columns.find((c) => c.accessorKey === targetKey);
        if (
          !targetRow ||
          !targetConfig ||
          isCellEditable(targetConfig, targetRow)
        ) {
          // Keep the keyboard-open marker on the cell the sweep actually lands on;
          // otherwise focus returns to where the sweep started, rows away.
          if (keyboardOpenRef.current) {
            keyboardOpenRef.current = `${rowIds[position.row]}::${String(targetKey)}`;
          }
          handleCellClick(rowIds[position.row], targetKey);
          return;
        }
      }

      endEditing();
    },
    [editingCell, handleSave, columns, data, handleCellClick]
  );

  const gridNavigate = enableGridKeyboardNav ? navigateGrid : undefined;

  const tableColumns: ColumnDef<T>[] = useMemo(() => {
    const baseColumns = columns.map((config, index) => {
      const columnSize = config.size || config.width || getColumnWidth(config);
      return {
        id: generateColumnId(config, index),
        accessorKey: config.accessorKey as string,
        header:
          typeof config.header === "string"
            ? config.header
            : () => config.header,
        size: columnSize,
        meta: {
          inputType: config.inputType,
        },
        cell: ({ row }: { row: Row<T> }) => {
          const rowId = row.original.id;
          if (!rowId) {
            console.warn("Row missing ID, skipping edit functionality");
            return <div>{i18n._({ id: "table.row.invalid", message: "Invalid row data" })}</div>;
          }

          // Editability is resolved per row — a predicate column is read-only on
          // rows that lack the backing record and editable on the rest.
          const rowEditable = isCellEditable(config, row.original);

          // Issue #1224: only a row the column cannot edit gets an explanation,
          // and only when the column supplies one. A `title` and not a Tooltip
          // component on purpose: an inert cell must stay out of the tab order
          // (see the `role`/`tabIndex` gating below), and the authoritative,
          // keyboard-reachable wording lives on the edit form's field
          // description. Do not promote this to a focusable Tooltip.
          const readOnlyTitle = rowEditable
            ? undefined
            : config.readOnlyTooltip?.(row.original);

          // The wrapper's own text is the rendered cell, which for a custom renderer
          // can be an icon or a placeholder — not enough to identify the row. Name it
          // by column plus current value so the announcement says what would be edited
          // and what it holds. getCellAccessibleValue converts the stored value to the
          // text the cell shows and keeps objects out of the name.
          //
          // Only an editable cell is exposed as a control, and the name costs two ICU
          // formats per cell on every render — a wide read-only table would pay for
          // hundreds of names it never renders.
          const cellAriaLabel = rowEditable
            ? i18n._({
                id: "table.cell.editAriaLabel",
                message: "Edit {column}: {value}",
                values: {
                  column: getColumnLabel(config),
                  value: getCellAccessibleValue(
                    config,
                    row.original,
                    i18n._({ id: "table.cell.emptyValue", message: "empty" }),
                    i18n.locale
                  ),
                },
              })
            : undefined;
          const cellKey = `${rowId}::${String(config.accessorKey)}`;
          // One lookup for both wrapper branches: the custom-renderer branch and
          // the default branch used to read the same key separately, which let the
          // two drift on which message wins a cell's title.
          const cellInvalidMessage = invalidCells.get(cellKey);

          // If the column has a custom cell renderer
          if (config.cell) {
            // If it's editable, wrap it in a clickable container
            if (rowEditable) {
              const isEditing =
                editingCell?.rowId === rowId &&
                editingCell?.column === config.accessorKey;

              if (isEditing) {
                // Compare in edit-space: `editValue` was produced by getEditValue
                // (see handleCellClick), so the baseline must go through it too.
                // Comparing an edit-space value against the raw row value makes
                // every cell open-and-blur look like a change and fire a
                // pointless save (e.g. "09:00" vs "2026-03-05T08:00:00.000Z").
                const originalValue = getComparableOriginal(row.original, config);

                return (
                  <EditableCell
                    key={`edit-${rowId}-${String(config.accessorKey)}`}
                    config={config}
                    value={editValue}
                    originalValue={originalValue}
                    onChange={(newValue) => {
                      setEditValue(newValue);
                    }}
                    onPendingChange={(v) => {
                      pendingEditRef.current = v;
                    }}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    validationError={validationError}
                    isSaving={isSaving}
                    editingCell={editingCell}
                    onGridNavigate={gridNavigate}
                  />
                );
              }

              return (
                <div
                  className={cn(
                    // min-h, not h: a custom renderer may return content taller
                    // than one line (e.g. a stacked badge pair). A fixed h-10
                    // centred that content in a 40px box and the enclosing
                    // TableCell's overflow-hidden then sliced the top off the
                    // first badge and the bottom off the second.
                    densityStyles.cellMinHeight,
                    "group px-2 flex items-center rounded transition-colors truncate border border-transparent",
                    "cursor-pointer hover:bg-accent/50",
                    flashCellKey === cellKey && "bg-success-muted",
                    // Right-align number fields, left-align everything else
                    config.inputType === "number"
                      ? "justify-end"
                      : "justify-start",
                    // A cell whose last entry failed validation stays flagged after the
                    // user navigates on — non-blocking validation must still leave a trace.
                    cellInvalidMessage && "border-destructive text-destructive",
                    // Same focus ring as the sortable header, so a keyboard user can
                    // see which cell they are on.
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                  )}
                  role="button"
                  tabIndex={0}
                  aria-haspopup={getCellHasPopup(config)}
                  aria-label={cellAriaLabel}
                  data-cell-key={cellKey}
                  onKeyDown={(event) =>
                    handleCellKeyDown(event, rowId, config.accessorKey)
                  }
                  onClick={(event) => {
                    if (isInteractiveChildEvent(event)) return;
                    // A mouse-opened edit must never restore focus (the pointer
                    // decides where focus goes next).
                    keyboardOpenRef.current = null;
                    handleCellClick(rowId, config.accessorKey);
                  }}
                  title={cellInvalidMessage || String(row.original[config.accessorKey] || "")}
                  aria-invalid={cellInvalidMessage ? true : undefined}
                >
                  {config.inputType === "number" && <EditHint position="start" />}
                  {config.cell({
                    row,
                    getValue: () => row.original[config.accessorKey],
                  })}
                  {config.inputType !== "number" && <EditHint position="end" />}
                </div>
              );
            }

            // Not editable, wrap in consistent padding
            return (
              <div
                className={cn(
                  // min-h, not h — see the editable branch above.
                  densityStyles.cellMinHeight,
                  "px-2 flex items-center rounded transition-colors truncate border border-transparent",
                  "cursor-default",
                  // Right-align number fields, left-align everything else
                  config.inputType === "number"
                    ? "justify-end"
                    : "justify-start"
                )}
                title={cellInvalidMessage || readOnlyTitle}
              >
                {config.cell({
                  row,
                  getValue: () => row.original[config.accessorKey],
                })}
              </div>
            );
          }

          // Otherwise, use the default editable cell behavior
          const isEditing =
            editingCell?.rowId === rowId &&
            editingCell?.column === config.accessorKey;

          if (isEditing) {
            // Compare in edit-space — see the note on the other EditableCell site.
            const originalValue = getComparableOriginal(row.original, config);

            return (
              <EditableCell
                key={`edit-${rowId}-${String(config.accessorKey)}`}
                config={config}
                value={editValue}
                originalValue={originalValue}
                onChange={(newValue) => {
                  setEditValue(newValue);
                }}
                onPendingChange={(v) => {
                  pendingEditRef.current = v;
                }}
                onSave={handleSave}
                onCancel={handleCancel}
                validationError={validationError}
                isSaving={isSaving}
                editingCell={editingCell}
                onGridNavigate={gridNavigate}
              />
            );
          }

          return (
            <div
              className={cn(
                densityStyles.cellHeight,
                "group px-2 flex items-center rounded transition-colors truncate border border-transparent",
                rowEditable
                  ? "cursor-pointer hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                  : "cursor-default",
                flashCellKey === cellKey && "bg-success-muted",
                // Right-align number fields, left-align everything else
                config.inputType === "number" ? "justify-end" : "justify-start",
                // See the note on the custom-cell branch: a failed entry stays flagged.
                cellInvalidMessage && "border-destructive text-destructive"
              )}
              // This wrapper serves the read-only case too, so every keyboard and
              // AT attribute is gated on rowEditable — an inert cell must stay out
              // of the tab order.
              role={rowEditable ? "button" : undefined}
              tabIndex={rowEditable ? 0 : undefined}
              aria-haspopup={rowEditable ? getCellHasPopup(config) : undefined}
              aria-label={cellAriaLabel}
              data-cell-key={rowEditable ? cellKey : undefined}
              onKeyDown={
                rowEditable
                  ? (event) =>
                      handleCellKeyDown(event, rowId, config.accessorKey)
                  : undefined
              }
              onClick={(event) => {
                if (rowEditable && !isInteractiveChildEvent(event)) {
                  // See the custom-cell branch: a mouse-opened edit restores nothing.
                  keyboardOpenRef.current = null;
                  handleCellClick(rowId, config.accessorKey);
                }
              }}
              // A validation message still wins: it reports a failure the user
              // just caused, which outranks a standing explanation.
              title={cellInvalidMessage || readOnlyTitle || String(row.original[config.accessorKey] || "")}
              aria-invalid={cellInvalidMessage ? true : undefined}
            >
              <div
                className={cn(
                  "flex items-center w-full",
                  // Right-align number fields, left-align everything else; the
                  // trailing icons push themselves right with ml-auto.
                  config.inputType === "number"
                    ? "justify-end"
                    : "justify-start"
                )}
              >
                {(() => {
                  // Calculate displayValue and rawEmailForMailto here
                  const originalValue = row.original[config.accessorKey];
                  let displayValue = "";

                  // Attempt to parse date/timestamp strings into Date objects for consistent formatting
                  let dateObject: Date | null = null;
                  if (originalValue instanceof Date) {
                    dateObject = originalValue;
                  } else if (typeof originalValue === "string") {
                    try {
                      const parsed = new Date(originalValue);
                      if (!isNaN(parsed.getTime())) {
                        dateObject = parsed;
                      }
                    } catch {
                      // Ignore parsing errors, will fall back to string display
                    }
                  }

                  // Check for custom formatter first (highest priority)
                  if (config.formatter) {
                    displayValue = config.formatter(
                      originalValue,
                      row.original
                    );
                  }
                  // Then apply type-specific formatting
                  else if (
                    (config.inputType === "select" || config.inputType === "enhanced-select") &&
                    config.selectOptions &&
                    originalValue
                  ) {
                    const option = config.selectOptions.find(
                      (opt) => opt.value === originalValue
                    );
                    displayValue = option
                      ? option.label
                      : String(originalValue);
                  } else if (
                    dateObject && // Check if we have a valid Date object
                    typeof config.accessorKey === "string" &&
                    (config.accessorKey.includes("At") ||
                      config.accessorKey.includes("Time"))
                  ) {
                    // Timestamps read dd.MM.yyyy HH:mm in the organisation's zone.
                    displayValue = formatDateTime(dateObject);
                  } else if (dateObject && config.inputType === "date") {
                    displayValue = formatCellDate(
                      dateObject,
                      config.dateFormat,
                      i18n.locale
                    );
                  } else if (
                    config.inputType === "number" &&
                    config.numberFormat &&
                    originalValue != null
                  ) {
                    // Apply number formatting
                    const locale = i18n.locale || "en";
                    const localeString = locale === "en" ? "en-US" : locale;
                    displayValue = new Intl.NumberFormat(
                      localeString,
                      config.numberFormat
                    ).format(Number(originalValue));
                  } else {
                    displayValue = String(originalValue || "");
                  }

                  const rawEmailForMailto = String(
                    row.original[config.accessorKey] || ""
                  );

                  const emailLabel =
                    config.inputType === "email"
                      ? i18n._({
                          id: "table.cell.sendEmail",
                          message: "Send email to {email}",
                          values: { email: displayValue },
                        })
                      : undefined;
                  // Date cells already carry a calendar glyph, so the pencil
                  // would only repeat it.
                  const showEditHint = rowEditable && config.inputType !== "date";
                  const hasTrailingIcons =
                    config.inputType === "email" ||
                    config.inputType === "date" ||
                    (showEditHint && config.inputType !== "number");

                  return (
                    <>
                      {showEditHint && config.inputType === "number" && (
                        <EditHint position="start" />
                      )}
                      <span
                        className={cn(
                          "truncate",
                          // Apply monospace font to dates, timestamps, and configured fields
                          config.inputType === "date" ||
                            config.useMonospaceFont ||
                            (typeof config.accessorKey === "string" &&
                              (config.accessorKey.includes("At") ||
                                config.accessorKey.includes("Time")))
                            ? "font-mono text-sm"
                            : "",
                          // Add left padding for editable date fields to align with DatePicker in edit mode
                          config.inputType === "date" && rowEditable
                            ? "pl-2"
                            : ""
                        )}
                        // Same precedence as the wrapper. The span sits INSIDE the
                        // wrapper, so a `title` here wins for a pointer over the
                        // rendered value — which is where a user hovers. Leaving it
                        // on the raw display value hid every readOnlyTooltip.
                        title={cellInvalidMessage || readOnlyTitle || displayValue}
                      >
                        {displayValue}
                      </span>

                      {hasTrailingIcons && (
                        <div className="ml-auto flex shrink-0 items-center gap-1 pl-2">
                          {showEditHint && config.inputType !== "number" && (
                            <Pencil
                              aria-hidden="true"
                              data-edit-hint=""
                              className="h-3.5 w-3.5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
                            />
                          )}
                          {config.inputType === "email" && (
                            <button
                              type="button"
                              title={emailLabel}
                              aria-label={emailLabel}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (
                                  rawEmailForMailto &&
                                  emailRegex.test(rawEmailForMailto)
                                ) {
                                  window.location.href = `mailto:${encodeURIComponent(
                                    rawEmailForMailto
                                  )}`;
                                } else {
                                  toast.error(
                                    i18n._({
                                      id: "table.cell.invalidEmail",
                                      message: "Invalid email address format.",
                                    })
                                  );
                                  console.warn(
                                    "Attempted to mail invalid email:",
                                    rawEmailForMailto
                                  );
                                }
                              }}
                              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                              <Mail className="h-4 w-4" aria-hidden="true" />
                            </button>
                          )}
                          {config.inputType === "date" && (
                            <CalendarIcon
                              aria-hidden="true"
                              className="h-4 w-4 text-muted-foreground"
                            />
                          )}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          );
        },
        enableSorting: config.sortable !== false,
        enableHiding: config.enableHiding !== false, // Default to true unless explicitly disabled
        enableColumnFilter:
          enableFilters && filterConfig && !!filterConfig[config.accessorKey],
        filterFn: (() => {
          // Assign custom filter function based on filter type
          if (!filterConfig) return undefined;
          const filterType = filterConfig[config.accessorKey];
          if (filterType === "select") {
            // Use labelsMultiSelect for array-valued columns (labels)
            if (config.accessorKey === "labels") return "labelsMultiSelect" as FilterFnOption<T>;
            return "multiSelect" as FilterFnOption<T>;
          }
          if (filterType === "date") return "dateRange" as FilterFnOption<T>;
          return undefined;
        })() as FilterFnOption<T> | undefined,
      };
    });

    // Add selection column if enabled
    if (enableSelection) {
      const selectionColumn: ColumnDef<T> = {
        id: "select",
        header: ({ table }) => (
          <div className={cn("flex items-center justify-start w-full pl-1.5", densityStyles.header)}>
            <Checkbox
              checked={
                table.getIsAllPageRowsSelected() ||
                (table.getIsSomePageRowsSelected() && "indeterminate")
              }
              onCheckedChange={(value) =>
                table.toggleAllPageRowsSelected(!!value)
              }
              aria-label={i18n._({
                id: "table.selection.selectAll",
                message: "Select all rows on this page",
              })}
              className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
            />
          </div>
        ),
        cell: ({ row }) => (
          <div className="flex items-center justify-start w-full h-full pl-1.5">
            <Checkbox
              checked={row.getIsSelected()}
              onCheckedChange={(value) => row.toggleSelected(!!value)}
              aria-label={i18n._({
                id: "table.selection.selectRow",
                message: "Select row",
              })}
              className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
            />
          </div>
        ),
        enableSorting: false,
        enableHiding: false, // Selection column should always be visible
        size: 35,
      };

      return [selectionColumn, ...baseColumns];
    }

    return baseColumns;
    // `i18n.locale` rather than the `i18n` instance: the instance reference is
    // stable across locale switches, so depending on it would keep serving
    // header and aria strings in the previous language after the user changes
    // locale. exhaustive-deps only sees the instance, hence the disable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    columns,
    getColumnWidth,
    editingCell,
    editValue,
    isSaving,
    validationError,
    invalidCells,
    handleSave,
    handleCancel,
    handleCellClick,
    handleCellKeyDown,
    enableSelection,
    enableFilters,
    filterConfig,
    i18n,
    i18n.locale,
    gridNavigate,
    densityStyles,
    flashCellKey,
  ]);

  // Handle column order changes
  const handleColumnOrderChange = useCallback(
    (
      updaterOrValue:
        | ColumnOrderState
        | ((prev: ColumnOrderState) => ColumnOrderState)
    ) => {
      const resolvedValue =
        typeof updaterOrValue === "function"
          ? updaterOrValue(columnOrder)
          : updaterOrValue;

      // Get all current column IDs for validation
      const allColumnIds = tableColumns
        .map((col) => col.id || "")
        .filter((id) => id !== "");

      // Validate the proposed order to prevent corruption
      const validatedOrder = validateColumnOrder(resolvedValue, allColumnIds);

      // Ensure fixed columns are always first
      const fixedIds = enableSelection ? ["select", "avatar"] : ["avatar"];
      const fixedInOrder = fixedIds.filter((id) => validatedOrder.includes(id));
      const nonFixedInOrder = validatedOrder.filter(
        (id) => !fixedIds.includes(id)
      );
      const finalOrder = [...fixedInOrder, ...nonFixedInOrder];

      setColumnOrder(finalOrder);

      // Persist to localStorage if tableId is provided
      if (tableId) {
        updatePersistedOrder(finalOrder);
      }

      // Notify external handler if provided
      if (onColumnOrderChange) {
        onColumnOrderChange(finalOrder);
      }
    },
    [
      columnOrder,
      tableId,
      updatePersistedOrder,
      onColumnOrderChange,
      enableSelection,
      tableColumns,
    ]
  );

  const table = useReactTable({
    data,
    columns: tableColumns,
    state: {
      sorting,
      pagination,
      rowSelection,
      globalFilter: _internalGlobalFilter,
      columnFilters: _internalColumnFilters,
      columnVisibility,
      columnOrder,
    },
    onSortingChange: (updater) => {
      const newSorting = typeof updater === "function" ? updater(sorting) : updater;
      setSorting(newSorting);
      resetPageIndex();
      // Call external callback for server-side sorting
      if (externalOnSortingChange) {
        externalOnSortingChange(newSorting);
      }
    },
    onPaginationChange: (updater) => {
      // Calculate new pagination for persistence check
      const newPagination =
        typeof updater === "function" ? updater(pagination) : updater;

      // Persist page size if it changes
      if (tableId && newPagination.pageSize !== pagination.pageSize) {
        updatePersistedPageSize(newPagination.pageSize);
      }

      // Use external handler for server-side pagination, internal for client-side
      if (serverSidePagination && externalOnPaginationChange) {
        externalOnPaginationChange(updater);
      } else {
        setInternalPagination(updater);
      }
    },
    onRowSelectionChange: handleRowSelectionChange,
    onGlobalFilterChange: handleGlobalFilterChange,
    onColumnFiltersChange: handleColumnFiltersChange,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    onColumnOrderChange: handleColumnOrderChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    globalFilterFn: (row, columnId, value, addMeta) =>
      fuzzyFilter(row, columnId, value, addMeta, {
        columns,
        locale: i18n.locale || "en",
      }),
    filterFns: {
      fuzzy: (row, columnId, value, addMeta) =>
        fuzzyFilter(row, columnId, value, addMeta, {
          columns,
          locale: i18n.locale || "en",
        }),
      multiSelect: multiSelectFilter,
      labelsMultiSelect: labelsFilter,
      dateRange: dateRangeFilter,
    },
    getRowId: (row, index) => row.id ?? `row-${index}`, // Ensure each row has a deterministic ID
    enableRowSelection: enableSelection,
    enableGlobalFilter: enableSearch,
    enableColumnFilters: enableFilters,
    // Server-side pagination configuration — disable client-side filtering/sorting when server handles it
    manualPagination: serverSidePagination,
    manualFiltering: serverSidePagination,
    manualSorting: serverSidePagination,
    ...(serverSidePagination && totalRows !== undefined && { rowCount: totalRows }),
    // Disable column resizing and auto-sizing to prevent flickering
    enableColumnResizing: false,
    defaultColumn: {
      minSize: 10, // Allow very small columns like checkboxes
      maxSize: 9999, // Allow columns to grow as needed
    },
    // Off on purpose: TanStack's auto reset also fires on every `data` change,
    // which would send the user to page 1 after each inline save. Search, filter
    // and sort reset the page explicitly instead (`resetPageIndex`).
    autoResetPageIndex: false,
  });

  // Clamp the page index when the row set shrinks under it.
  //
  // `autoResetPageIndex: false` above is deliberate — it stops the table from
  // yanking the operator back to page 1 on every inline edit. Search, filter and
  // sort reset the page themselves, but a mutation that removes rows while on a
  // later page would still strand the user on a page that no longer exists: an
  // empty grid reading "11-6 of 6 rows · Page 2 of 1".
  //
  // Client-side pagination only: with serverSidePagination the page index is
  // owned by the caller, and correcting it here would fight that state.
  const currentPageIndex = table.getState().pagination.pageIndex;
  const resolvedPageCount = table.getPageCount();
  useEffect(() => {
    if (serverSidePagination) return;
    if (resolvedPageCount === 0) {
      // No rows at all — page 1 is the only honest position.
      if (currentPageIndex !== 0) table.setPageIndex(0);
      return;
    }
    if (currentPageIndex > resolvedPageCount - 1) {
      table.setPageIndex(resolvedPageCount - 1);
    }
  }, [currentPageIndex, resolvedPageCount, serverSidePagination, table]);

  // Keep grid navigation aligned with what is on screen. Assigned in an effect (not during
  // render) so the ref stays a pure post-commit mirror of the rendered row model; keyboard
  // events always fire after commit, so it is never read stale.
  const renderedRowIds = table
    .getRowModel()
    .rows.map((r) => (r.original as { id?: string }).id)
    .filter((id): id is string => !!id);
  useEffect(() => {
    visibleRowIdsRef.current = renderedRowIds;
  });

  // Same contract for the column axis: `getVisibleLeafColumns()` is the rendered order
  // with hidden columns already dropped, so grid navigation can never target a column
  // the user cannot see. Columns statically marked `editable: false` are excluded here;
  // predicate columns stay in the list and are resolved per row at landing time.
  const columnConfigById = useMemo(() => {
    const map = new Map<string, EditableCellConfig<T>>();
    columns.forEach((config, index) => {
      map.set(generateColumnId(config, index), config);
    });
    return map;
  }, [columns]);
  const renderedEditableColumnKeys = table
    .getVisibleLeafColumns()
    .map((column) => columnConfigById.get(column.id))
    .filter(
      (config): config is EditableCellConfig<T> =>
        !!config && config.editable !== false
    )
    .map((config) => config.accessorKey);
  useEffect(() => {
    editableColumnKeysRef.current = renderedEditableColumnKeys;
  });

  // Table body container ref for virtualization
  const tableBodyRef = useRef<HTMLDivElement>(null);

  // Setup virtualizer when enabled
  const rowVirtualizer = useVirtualizer({
    count: enableVirtualization ? table.getRowModel().rows.length : 0,
    getScrollElement: () => tableBodyRef.current,
    estimateSize: () => estimatedRowHeight,
    overscan,
    enabled: enableVirtualization,
  });

  // Get column filters and active filters info
  const columnFilters = table.getState().columnFilters;
  // Filter out entries with empty values (empty arrays or empty date ranges)
  const activeFilters = columnFilters
    .filter((filter) => {
      if (Array.isArray(filter.value)) {
        return filter.value.length > 0;
      }
      if (typeof filter.value === 'object' && filter.value !== null) {
        const dateValue = filter.value as { from?: Date; to?: Date };
        return dateValue.from || dateValue.to;
      }
      return filter.value != null;
    })
    .map((filter) => filter.id);
  const hasActiveFilters = activeFilters.length > 0;

  // Column labels for filter display
  const columnLabels = columns.reduce((acc, col) => {
    acc[col.accessorKey as string] = getHeaderText(col.header);
    return acc;
  }, {} as Record<string, string>);

  // Rows the user selected, remembered by id. A server-paged table holds one page
  // in `data`, so "Export selected (12)" after selecting on three pages would
  // otherwise export only the rows of the page on screen.
  const selectedRowCacheRef = useRef(new Map<string, T>());
  useEffect(() => {
    const cache = selectedRowCacheRef.current;
    for (const id of Array.from(cache.keys())) {
      if (!rowSelection[id]) cache.delete(id);
    }
    data.forEach((row, index) => {
      const id = row.id ?? `row-${index}`;
      if (rowSelection[id]) cache.set(id, row);
    });
  }, [rowSelection, data]);

  const [isExporting, setIsExporting] = useState(false);
  // "Export all matching" needs every matching row. A server-paged table has
  // them only through `exportAll`; without it the item would export one page
  // under an "all" label, so it is not offered.
  const canExportAll = !!exportAll || !serverSidePagination;

  const resolvedExportColumns = useMemo<CSVExportColumn<T>[]>(() => {
    if (exportColumns) return exportColumns;
    return table
      .getVisibleLeafColumns()
      .map((column) => columnConfigById.get(column.id))
      .filter((config): config is EditableCellConfig<T> => !!config)
      .map((config) => ({
        config,
        header: getHeaderText(config.header).trim(),
      }))
      // Avatar and action columns have no header and no exportable value.
      .filter(({ header }) => header !== "")
      .map(({ config, header }) => ({
        key: config.accessorKey,
        header,
        formatter: (value: unknown, row?: T) =>
          getExportCellText(config, value, row as T, i18n.locale),
      }));
    // The visible set changes with `columnVisibility` and `columnOrder`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportColumns, columnConfigById, columnVisibility, columnOrder, table, i18n.locale]);

  const runExport = async (scope: "all" | "selected") => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      let rows: T[];
      if (scope === "selected") {
        const cache = selectedRowCacheRef.current;
        const missing = selectedRowIds.filter((id) => !cache.has(id));
        // A caller-controlled selection may name rows this table never rendered.
        const fetched = missing.length > 0 && exportAll ? await exportAll() : [];
        const fetchedById = new Map(
          fetched.filter((row) => row.id).map((row) => [row.id as string, row])
        );
        rows = selectedRowIds
          .map((id) => cache.get(id) ?? fetchedById.get(id))
          .filter((row): row is T => row !== undefined);
      } else if (exportAll) {
        rows = await exportAll();
      } else {
        // Filtered and sorted, across all pages.
        rows = table.getPrePaginationRowModel().rows.map((row) => row.original);
      }

      if (rows.length === 0) {
        toast.info(
          i18n._({ id: "table.export.noRows", message: "No rows to export" })
        );
        return;
      }

      const stem = exportFileName ?? tableId ?? "export";
      const date = formatDateFns(new Date(), "yyyy-MM-dd");
      exportToCSV(
        rows,
        resolvedExportColumns,
        scope === "selected" ? `${stem}-selected-${date}` : `${stem}-${date}`
      );
      toast.success(
        i18n._({
          id: "table.export.success",
          message: "Exported {count, plural, one {# row} other {# rows}}",
          values: { count: rows.length },
        })
      );
    } catch (error) {
      logger.error("table export failed", { context: "BaseEditableTable", tableId, scope, error });
      toast.error(
        errorToastText(
          error,
          i18n,
          msg({ id: "table.export.failed", message: "Export failed" })
        )
      );
    } finally {
      setIsExporting(false);
    }
  };

  const hasSelection = enableSelection && selectedRowIds.length > 0;
  const visibleColumnCount = table.getVisibleLeafColumns().length;

  // Filter handlers
  const handleAddFilter = (columnId: string, value: FilterValue) => {
    const column = table.getColumn(columnId);
    if (column) {
      column.setFilterValue(value);
    }
  };

  const handleRemoveFilter = (columnId: string) => {
    const column = table.getColumn(columnId);
    if (column) {
      column.setFilterValue(undefined);
    }
  };

  const handleFilterChange = (columnId: string, value: FilterValue) => {
    const column = table.getColumn(columnId);
    if (column) {
      column.setFilterValue(value);
    }
  };

  const handleClearAllFilters = () => {
    table.resetColumnFilters();
  };

  return (
    <div className="space-y-4">
      {/* Search and Filter Controls - Same row */}
      <div className="flex flex-wrap items-center gap-3">
        {toolbarLeft}

        {enableFilters && filterConfig && (
          <FilterDropdown
            filterConfig={filterConfig}
            columnLabels={columnLabels}
            activeFilters={activeFilters}
            onFilterAdd={handleAddFilter}
            table={table}
            columns={columns}
          />
        )}

        {enableSearch && (
          <TableSearch
            value={globalFilter}
            onChange={handleGlobalFilterChange}
            placeholder={searchPlaceholder}
            className={`flex-1 ${searchMaxWidth ?? "max-w-sm"}`}
          />
        )}

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearAllFilters}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4 mr-1" />
            <Trans id="table.filter.clearAll">Clear all filters</Trans>
          </Button>
        )}

        {toolbarRight}

        {((isFetching && !isLoading) ||
          enableExport ||
          enableColumnVisibility ||
          enableColumnOrdering) && (
          <div className="ml-auto flex items-center gap-2">
            {/* A background refetch keeps the rows readable: a spinner, no dimming. */}
            {isFetching && !isLoading && (
              <span
                role="status"
                aria-label={i18n._({ id: 'common.loading', message: 'Loading...' })}
                className="inline-flex"
                data-testid="table-fetching-indicator"
              >
                <Loader2
                  className="h-4 w-4 animate-spin text-muted-foreground"
                  aria-hidden="true"
                />
              </span>
            )}

            {enableExport && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-10"
                    disabled={isExporting || (!canExportAll && !hasSelection)}
                  >
                    {isExporting ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" aria-hidden="true" />
                    )}
                    {isExporting
                      ? i18n._({ id: "table.export.exporting", message: "Exporting..." })
                      : i18n._({ id: "table.export.menu", message: "Export" })}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {canExportAll && (
                    <DropdownMenuItem onSelect={() => void runExport("all")}>
                      {i18n._({ id: "table.export.allMatching", message: "Export all matching" })}
                    </DropdownMenuItem>
                  )}
                  {hasSelection && (
                    <DropdownMenuItem onSelect={() => void runExport("selected")}>
                      {i18n._({
                        id: "table.export.selected",
                        message: "Export selected ({count})",
                        values: { count: selectedRowIds.length },
                      })}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {(enableColumnVisibility || enableColumnOrdering) && (
              <TableColumnManager
                table={table}
                onReset={tableId ? clearPreferences : undefined}
                density={density}
                onDensityChange={tableId ? updateDensity : undefined}
              />
            )}
          </div>
        )}
      </div>

      {/* Announces a saved cell; the success tint is visual only. */}
      <div role="status" aria-live="polite" className="sr-only">
        {saveAnnouncement}
      </div>

      {/* Active Filters Row - Below search/selector, left-aligned with table */}
      {enableFilters && hasActiveFilters && (
        <div className="flex flex-wrap gap-2">
          {columnFilters.map((filter) => {
            const column = table.getColumn(filter.id);
            const filterType =
              filterConfig![filter.id as keyof typeof filterConfig];
            const columnLabel = columnLabels[filter.id] || filter.id;
            const columnConfig = columns.find(
              (col) => col.accessorKey === filter.id
            );

            if (!column || !filterType) return null;

            return (
              <FilterTag
                key={filter.id}
                columnLabel={columnLabel}
                filterValue={filter.value as FilterValue}
                column={column}
                filterType={filterType}
                selectOptions={columnConfig?.selectOptions}
                onChange={(value) => handleFilterChange(filter.id, value)}
                onRemove={() => handleRemoveFilter(filter.id)}
              />
            );
          })}
        </div>
      )}

      {/* Custom content below toolbar (e.g. external active filter badges) */}
      {toolbarBelowRow}

      {/* Selection Actions - Show when rows are selected and renderSelectionActions is provided */}
      {enableSelection &&
        selectedRowIds.length > 0 &&
        renderSelectionActions && (
          <div className="flex items-center justify-start w-full">
            <div className="bg-muted/30 border border-border rounded-lg p-2 flex items-center space-x-2">
              {renderSelectionActions(selectedRowIds, clearSelection)}
            </div>
          </div>
        )}

      {/* One scroll box for both axes, capped to the viewport, so the sticky
          header stays in view. `scrollContainer={false}` stops the Table
          primitive from nesting a second one, which would break `sticky`. */}
      <div
        ref={tableContainerRef}
        className="rounded-lg border border-border bg-card shadow-sm overflow-hidden relative"
        data-table-container="true"
      >
        <div
          ref={enableVirtualization ? tableBodyRef : undefined}
          data-testid="table-scroll-container"
          className={cn(
            "overflow-auto",
            enableVirtualization
              ? "max-h-[600px]"
              : "max-h-[max(20rem,calc(100dvh-20rem))]"
          )}
        >
          <Table className="w-full" scrollContainer={false}>
            {/* Collapsed table borders do not travel with a sticky header, so
                the bottom rule is an inset shadow on each header cell. */}
            <TableHeader className="sticky top-0 z-10 bg-background [&_th]:shadow-[inset_0_-1px_0_hsl(var(--border))]">
              <TableRow className="bg-muted/50 hover:bg-muted/70">
                {table.getHeaderGroups().map((headerGroup) =>
                  headerGroup.headers
                    .filter((header) => header.column.getIsVisible())
                    .map((header) => (
                      <TableHead
                        key={header.id}
                        className="text-foreground font-semibold whitespace-nowrap py-2 px-2"
                        style={{
                          minWidth: `${header.getSize()}px`,
                          ...(header.column.columnDef.size ? { width: `${header.getSize()}px` } : {}),
                        }}
                        aria-sort={
                          header.column.getCanSort()
                            ? header.column.getIsSorted() === "asc"
                              ? "ascending"
                              : header.column.getIsSorted() === "desc"
                                ? "descending"
                                : "none"
                            : undefined
                        }
                      >
                        {header.isPlaceholder ? null : (
                          <div
                            role={
                              header.column.getCanSort() ? "button" : undefined
                            }
                            tabIndex={header.column.getCanSort() ? 0 : undefined}
                            onKeyDown={
                              header.column.getCanSort()
                                ? (event) => {
                                    if (
                                      event.key === "Enter" ||
                                      event.key === " "
                                    ) {
                                      event.preventDefault();
                                      header.column
                                        .getToggleSortingHandler()
                                        ?.(event);
                                    }
                                  }
                                : undefined
                            }
                            className={cn(
                              "flex items-center space-x-2",
                              densityStyles.header,
                              header.column.getCanSort() &&
                                "cursor-pointer select-none hover:text-foreground/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded-sm",
                              // Add the same frame as cell content for alignment (except checkbox)
                              header.column.id !== "select"
                                ? cn(
                                    "px-2 rounded border border-transparent",
                                    // Right-align number headers to match cells, left-align everything else
                                    header.column.columnDef.meta?.inputType ===
                                      "number"
                                      ? "justify-end"
                                      : "justify-start"
                                  )
                                : "justify-start"
                            )}
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            <span
                              className={cn(
                                // Add left padding for date columns to align with DatePicker button
                                header.column.id === "birthdate" ||
                                  header.column.id.includes("date") ||
                                  header.column.id.includes("Date")
                                  ? "pl-2"
                                  : ""
                              )}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                            </span>
                            {header.column.getCanSort() && (
                              <div className="flex flex-col" aria-hidden="true">
                                {header.column.getIsSorted() === "asc" ? (
                                  <ChevronUp className="h-4 w-4" />
                                ) : header.column.getIsSorted() === "desc" ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <div className="h-4 w-4 flex flex-col justify-center">
                                    <ChevronUp className="h-2 w-4 opacity-30" />
                                    <ChevronDown className="h-2 w-4 opacity-30" />
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </TableHead>
                    ))
                )}
              </TableRow>
            </TableHeader>
            <TableBody aria-busy={isLoading || undefined}>
              {isLoading ? (
                <TableSkeletonRows
                  rows={Math.min(table.getState().pagination.pageSize, 10)}
                  columns={visibleColumnCount}
                  cellClassName={densityStyles.cell}
                />
              ) : table.getRowModel().rows?.length ? (
                enableVirtualization ? (
                  // Virtualized rendering
                  <div
                    style={{
                      height: `${rowVirtualizer.getTotalSize()}px`,
                      width: "100%",
                      position: "relative",
                    }}
                  >
                    {rowVirtualizer.getVirtualItems().map((virtualItem) => {
                      const row = table.getRowModel().rows[virtualItem.index];
                      return (
                        <TableRow
                          key={row.id}
                          data-state={row.getIsSelected() && "selected"}
                          onClick={
                            onRowClick
                              ? (e) => handleRowClick(e, row.original)
                              : undefined
                          }
                          className={cn(
                            "hover:bg-muted/30 transition-colors absolute w-full",
                            onRowClick && "cursor-pointer",
                            virtualItem.index % 2 === 0
                              ? "bg-background"
                              : "bg-muted/20",
                            rowClassName?.(row.original)
                          )}
                          style={{
                            height: `${virtualItem.size}px`,
                            transform: `translateY(${virtualItem.start}px)`,
                          }}
                        >
                          {row.getVisibleCells().map((cell) => {
                            return (
                              <TableCell
                                key={cell.id}
                                className={cn(
                                  "whitespace-nowrap overflow-hidden text-ellipsis",
                                  densityStyles.cell
                                )}
                                style={{
                                  minWidth: `${cell.column.getSize()}px`,
                                  ...(cell.column.columnDef.size ? { width: `${cell.column.getSize()}px` } : {}),
                                }}
                              >
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </div>
                ) : (
                  // Non-virtualized rendering
                  table.getRowModel().rows.map((row, index) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && "selected"}
                      onClick={
                        onRowClick
                          ? (e) => handleRowClick(e, row.original)
                          : undefined
                      }
                      className={cn(
                        "hover:bg-muted/30 transition-colors",
                        onRowClick && "cursor-pointer",
                        index % 2 === 0 ? "bg-background" : "bg-muted/20",
                        rowClassName?.(row.original)
                      )}
                    >
                      {row.getVisibleCells().map((cell) => {
                        return (
                          <TableCell
                            key={cell.id}
                            className={cn(
                              "whitespace-nowrap overflow-hidden text-ellipsis",
                              densityStyles.cell
                            )}
                            style={{ minWidth: `${cell.column.getSize()}px` }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                )
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={
                      enableSelection ? columns.length + 1 : columns.length
                    }
                    className="text-center text-muted-foreground"
                  >
                    {/*
                      `hasActiveFilters` only sees the table's INTERNAL client-side
                      column filters. `hasExternalFilters` lets a server-filtered
                      caller say "I am filtered" — without it such a page renders the
                      first-use state over a filtered-empty result. Both default to
                      today's behaviour when the caller passes nothing.
                    */}
                    {hasActiveFilters || globalFilter || hasExternalFilters ? (
                      filteredEmptyState ?? (
                      // No matches for the active filters/search — the table knows
                      // its filter state, so the copy points at adjusting them.
                      <EmptyState
                        icon={<SearchX className="h-10 w-10" />}
                        title={
                          <Trans id="table.empty.filtered.title">
                            No matching results
                          </Trans>
                        }
                        description={
                          <Trans id="table.empty.filtered.description">
                            Try adjusting your search or filters.
                          </Trans>
                        }
                        action={
                          hasActiveFilters ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleClearAllFilters}
                            >
                              <X className="h-4 w-4 mr-1" />
                              <Trans id="table.filter.clearAll">
                                Clear all filters
                              </Trans>
                            </Button>
                          ) : undefined
                        }
                      />
                      )
                    ) : (
                      emptyState ?? (
                      // Genuinely nothing to show yet.
                      <EmptyState
                        icon={<Inbox className="h-10 w-10" />}
                        title={
                          <Trans id="common.empty.noResults">No results found</Trans>
                        }
                        description={
                          <Trans id="table.empty.default.description">
                            Nothing here yet.
                          </Trans>
                        }
                        action={
                          onAdd ? (
                            <Button size="sm" onClick={onAdd}>
                              <Plus className="h-4 w-4 mr-1" aria-hidden="true" />
                              {addLabel ?? <Trans id="common.add">Add</Trans>}
                            </Button>
                          ) : undefined
                        }
                      />
                      )
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination Controls - Outside table scroll, constrained to page width.
          Wraps in a narrow container (a table inside a card) instead of overflowing it. */}
      {!hidePagination && (
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 py-2">
        {/* Left: Row count info */}
        <div className="text-sm text-muted-foreground flex-shrink-0 whitespace-nowrap">
          {(() => {
            const currentPageRows = table.getRowModel().rows.length;
            // In server mode the model holds one page; the server's count is the total.
            const rowTotal =
              serverSidePagination && typeof totalRows === 'number' ? totalRows : table.getFilteredRowModel().rows.length;
            const { pageIndex, pageSize } = table.getState().pagination;

            if (isLoading) return null;
            if (rowTotal === 0) {
              return <Trans id="table.noRows">No rows</Trans>;
            }

            const startRow = pageIndex * pageSize + 1;
            const endRow = Math.min(startRow + currentPageRows - 1, rowTotal);

            return (
              <Trans
                id="table.rowRange"
                values={{ startRow, endRow, totalRows: rowTotal }}
                message="{startRow}-{endRow} of {totalRows, plural, one {# row} other {# rows}}"
              />
            );
          })()}
        </div>

        {/* Right: Rows per page + Page navigation - Grouped more tightly */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {/* Rows per page */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-muted-foreground flex-shrink-0 whitespace-nowrap">
              <Trans id="table.rowsPerPage">Rows per page:</Trans>
            </span>
            <Select
              value={`${table.getState().pagination.pageSize}`}
              onValueChange={(value) => {
                table.setPageSize(Number(value));
              }}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent side="top">
                {/* The current page size must be one of the options, otherwise
                    Radix finds no matching item and the trigger renders blank.
                    Tables that pass a custom `defaultPageSize` (12, 5, unpaged
                    sizes) hit that on first paint. */}
                {pageSizeOptions(table.getState().pagination.pageSize).map(
                  (pageSize) => (
                    <SelectItem key={pageSize} value={`${pageSize}`}>
                      {pageSize}
                    </SelectItem>
                  ),
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Page navigation */}
          <div className="flex items-center space-x-2">
            <div className="text-sm text-muted-foreground flex-shrink-0 whitespace-nowrap">
              <Trans
                id="table.pageNavigation"
                values={{
                  currentPage: table.getState().pagination.pageIndex + 1,
                  totalPages: table.getPageCount(),
                }}
                message="Page {currentPage} of {totalPages}"
              />
            </div>
            {/* Icon-only controls need accessible names — the glyph alone
                leaves them unlabelled for screen readers and unaddressable
                from tests. */}
            <div className="flex items-center space-x-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
                className="h-8 w-8 p-0"
                aria-label={i18n._({
                  id: "table.pagination.firstPage",
                  message: "First page",
                })}
              >
                <ChevronsLeft className="h-4 w-4" aria-hidden="true" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="h-8 w-8 p-0"
                aria-label={i18n._({
                  id: "table.pagination.previousPage",
                  message: "Previous page",
                })}
              >
                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="h-8 w-8 p-0"
                aria-label={i18n._({
                  id: "table.pagination.nextPage",
                  message: "Next page",
                })}
              >
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
                className="h-8 w-8 p-0"
                aria-label={i18n._({
                  id: "table.pagination.lastPage",
                  message: "Last page",
                })}
              >
                <ChevronsRight className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}
